/**
 * 端末の「最後に使った表示言語」(admin-english-toggle-20260924 phase 2 p2-28 / ADR-081 の追記)。
 *
 * ログイン画面と SSO の戻り画面は、利用者が決まる前 (管理画面の shell = `AdminLocaleProvider` の外) に
 * 描くので、利用者ごとの保存 (`acecollect_admin_locale:{userId}`) を読めない。そこで、この端末で
 * 最後に管理画面を表示した言語を別のキー `acecollect_admin_locale:last` に保存し、ログイン画面はそれで描く。
 *
 * - 書くのは管理画面の shell ({@link LastUsedLocaleRecorder}、表示言語が決まるたび) と、ログイン画面の
 *   切替ボタン (利用者が選んだとき)。読み書きは `localeStorage.ts` と同じく try/catch (読めなければ日本語)。
 * - 利用者ごとの表示言語は変えない (ログインした後は、その職員の保存 → サーバーの個人設定 → 日本語の順で決まる)。
 * - キーの `last` は利用者 ID (Cognito の sub = UUID) と重ならない。
 * - 依頼者ポータルは使わない (常に日本語)。
 */
import { useEffect, type ReactNode } from 'react';
import { LocaleProvider, useLocale } from './LocaleProvider';
import { readStoredLocale, writeStoredLocale } from './localeStorage';
import type { Locale } from './types';

/** 端末の最後に使った言語を保存する単位 (`localeStorageKey(LAST_USED_LOCALE_SCOPE)` = `acecollect_admin_locale:last`)。 */
export const LAST_USED_LOCALE_SCOPE = 'last';

/** 端末の最後に使った言語。無い / 読めない / 未知の値は null (呼び出し側で日本語)。 */
export function readLastUsedLocale(): Locale | null {
  return readStoredLocale(LAST_USED_LOCALE_SCOPE);
}

/** 端末の最後に使った言語を保存する。書けなかったときは false (表示はそのまま続ける)。 */
export function writeLastUsedLocale(locale: Locale): boolean {
  return writeStoredLocale(LAST_USED_LOCALE_SCOPE, locale);
}

/**
 * 管理画面の shell の中に置き、表示中の言語を「最後に使った言語」として記録する (何も描かない)。
 * 利用者の切替・サーバーの個人設定の読み込み・別の職員のログインのいずれで言語が決まっても記録する。
 */
export function LastUsedLocaleRecorder(): null {
  const { locale } = useLocale();
  useEffect(() => {
    writeLastUsedLocale(locale);
  }, [locale]);
  return null;
}

/**
 * ログイン前の画面 (ログイン・SSO の戻り) を、端末の最後に使った言語で描く Provider。
 * 画面の中の切替 (`useLocale().setLocale`) は同じキーに保存する (サーバーには保存しない)。
 */
export function PreAuthLocaleProvider({ children }: { children: ReactNode }) {
  return <LocaleProvider storageScope={LAST_USED_LOCALE_SCOPE}>{children}</LocaleProvider>;
}
